import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';

/** Fail-safe so Control Now/Next Play is never blocked forever on a hung waiter. */
const STILL_FRAME_READY_TIMEOUT_MS = 2500;

type StillFrameReadyContextValue = {
  /** Register a still-frame asset. Call the returned settle() once when painted (or on unmount). */
  register: () => () => void;
};

const StillFrameReadyContext = createContext<StillFrameReadyContextValue | null>(null);

/**
 * Tracks still-frame media under Control Now/Next until all have painted.
 * If nothing registers after mount, reports ready immediately.
 *
 * Remounts an inner provider whenever `resetKey` changes so waiters re-register cleanly
 * (needed after cue advance when LiveKit layers keep the same url-less fingerprint shape).
 */
export function StillFrameReadyProvider({
  resetKey,
  onReadyChange,
  children,
}: {
  resetKey: string;
  onReadyChange?: (ready: boolean) => void;
  children: ReactNode;
}) {
  return (
    <StillFrameReadyProviderInner key={resetKey} onReadyChange={onReadyChange}>
      {children}
    </StillFrameReadyProviderInner>
  );
}

function StillFrameReadyProviderInner({
  onReadyChange,
  children,
}: {
  onReadyChange?: (ready: boolean) => void;
  children: ReactNode;
}) {
  const pendingRef = useRef(0);
  const reportedReadyRef = useRef(false);
  const onReadyChangeRef = useRef(onReadyChange);
  const timeoutIdRef = useRef<number | null>(null);

  useEffect(() => {
    onReadyChangeRef.current = onReadyChange;
  }, [onReadyChange]);

  const clearReadyTimeout = useCallback(() => {
    if (timeoutIdRef.current != null) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const scheduleReadyTimeout = useCallback(() => {
    clearReadyTimeout();
    timeoutIdRef.current = window.setTimeout(() => {
      timeoutIdRef.current = null;
      if (!reportedReadyRef.current) {
        reportedReadyRef.current = true;
        onReadyChangeRef.current?.(true);
      }
    }, STILL_FRAME_READY_TIMEOUT_MS);
  }, [clearReadyTimeout]);

  const reportReady = useCallback(() => {
    if (reportedReadyRef.current) {
      return;
    }
    reportedReadyRef.current = true;
    clearReadyTimeout();
    onReadyChangeRef.current?.(true);
  }, [clearReadyTimeout]);

  const reportNotReady = useCallback(() => {
    if (!reportedReadyRef.current && pendingRef.current > 0) {
      // Already waiting — keep the existing timeout.
      return;
    }
    reportedReadyRef.current = false;
    onReadyChangeRef.current?.(false);
    scheduleReadyTimeout();
  }, [scheduleReadyTimeout]);

  useEffect(() => {
    onReadyChangeRef.current?.(false);
    const rafId = requestAnimationFrame(() => {
      if (pendingRef.current === 0) {
        reportReady();
      }
    });
    scheduleReadyTimeout();
    return () => {
      cancelAnimationFrame(rafId);
      clearReadyTimeout();
    };
  }, [clearReadyTimeout, reportReady, scheduleReadyTimeout]);

  const register = useCallback(() => {
    pendingRef.current += 1;
    if (reportedReadyRef.current) {
      reportNotReady();
    }
    let settled = false;
    return () => {
      if (settled) {
        return;
      }
      settled = true;
      pendingRef.current = Math.max(0, pendingRef.current - 1);
      if (pendingRef.current === 0) {
        reportReady();
      }
    };
  }, [reportNotReady, reportReady]);

  const value = useMemo(() => ({ register }), [register]);

  return <StillFrameReadyContext.Provider value={value}>{children}</StillFrameReadyContext.Provider>;
}

/** Register this mount as a still-frame waiter; call the returned settle() when painted. */
export function useStillFrameReadyRegistration(enabled: boolean): (() => void) | undefined {
  const ctx = useContext(StillFrameReadyContext);
  const register = ctx?.register;
  const settleRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled || !register) {
      settleRef.current = null;
      return;
    }
    const settle = register();
    settleRef.current = settle;
    return () => {
      settle();
      if (settleRef.current === settle) {
        settleRef.current = null;
      }
    };
  }, [enabled, register]);

  const settle = useCallback(() => {
    settleRef.current?.();
  }, []);

  return enabled && register ? settle : undefined;
}
